import { ModuleConfig, ModuleField } from '../types';
import { buildListColumns } from '../utils/recordDisplayUtils';
import { vehicleTableColumns } from './vehicleTableColumns';
import { clientTableColumns } from './clientTableColumns';

const ACTIVE_INACTIVE = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const TRIP_TYPE_LABELS: Record<string, string> = {
  one_way: 'Single Trip',
  round_trip: 'Round Trip',
  multi_stop: 'Multi-stop Trip',
  multi_location: 'Multi-stop Trip',
  daily_recurring: 'Daily (Recurring)',
};

const STATUS_BY_MODULE: Record<string, ModuleField['options']> = {
  vehicles: [
    ...ACTIVE_INACTIVE,
    { label: 'Maintenance', value: 'maintenance' },
  ],
  trips: [
    { label: 'New', value: 'created' },
    { label: 'Assigned', value: 'assigned' },
    { label: 'Ongoing', value: 'in_progress' },
    { label: 'Completed', value: 'completed' },
    { label: 'Approved', value: 'approved' },
    { label: 'Cancelled', value: 'cancelled' },
  ],
};

const STATUS_FIELD: ModuleField = {
  name: 'status',
  label: 'Active or Inactive',
  type: 'select',
  required: true,
  options: ACTIVE_INACTIVE,
};

const NO_STATUS_MODULES = new Set([
  'fuel',
  'maintenance',
  'puncture',
  'battery_maintenance',
  'cash_memo',
  'fastag',
]);

const withStatus = (config: ModuleConfig): ModuleConfig => {
  if (NO_STATUS_MODULES.has(config.key)) {
    const fields = config.fields.filter((f) => f.name !== 'status');
    const columns = config.columns.filter((c) => c.field !== 'status');
    return { ...config, fields, columns, listColumns: buildListColumns(columns) };
  }

  const options = STATUS_BY_MODULE[config.key] || ACTIVE_INACTIVE;
  const hasStatusField = config.fields.some((f) => f.name === 'status');
  const fields = hasStatusField
    ? config.fields.map((f) => (
      f.name === 'status'
        ? {
          ...f,
          type: 'select' as const,
          options,
          required: true,
          label: options === ACTIVE_INACTIVE || (options?.length === 2 && options[0]?.value === 'active')
            ? 'Active or Inactive'
            : (f.label || 'Status'),
        }
        : f
    ))
    : [...config.fields, { ...STATUS_FIELD, options }];

  const hasStatusCol = config.columns.some((c) => c.field === 'status');
  const columns = hasStatusCol
    ? config.columns
    : [...config.columns, { field: 'status', header: 'Status' }];

  const listColumns = buildListColumns(columns);

  return { ...config, fields, columns, listColumns };
};

const FUEL_TYPES = [
  { label: 'Diesel', value: 'Diesel' },
  { label: 'Petrol', value: 'Petrol' },
  { label: 'CNG', value: 'CNG' },
  { label: 'Electric', value: 'Electric' },
  { label: 'LNG', value: 'LNG' },
];

const VEHICLE_SIZE_OPTIONS = [
  { label: '14 ft', value: '14 ft' },
  { label: '17 ft', value: '17 ft' },
  { label: '19 ft', value: '19 ft' },
  { label: '20 ft', value: '20 ft' },
  { label: '22 ft', value: '22 ft' },
  { label: '24 ft', value: '24 ft' },
  { label: '32 ft', value: '32 ft' },
  { label: '40 ft', value: '40 ft' },
  { label: 'Small', value: 'Small' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Large', value: 'Large' },
];

const DOC_STATUS_LABELS: Record<string, string> = {
  expired: '🔴 Expired',
  expiring_7: '🟠 7 days',
  expiring_30: '🟡 30 days',
  valid: '🟢 Valid',
  missing: '⚪ Missing',
  no_expiry: '🟢 Valid',
};

const coreModules: ModuleConfig[] = [
  {
    key: 'clients',
    title: 'Clients',
    path: '/clients',
    icon: 'Business',
    apiPath: '/clients',
    columns: clientTableColumns,
    fields: [
      { name: 'clientName', label: 'Client Name', required: true, placeholder: 'Enter client name' },
      { name: 'companyFullName', label: 'Company Full Name', required: true, placeholder: 'Enter company name' },
      { name: 'restHours', label: 'Rest Hours', type: 'number', required: true, placeholder: 'Enter rest hours (e.g. 10)' },
      { name: 'creditLimit', label: 'Credit Limit', type: 'number', placeholder: 'Enter credit limit' },
      { name: 'contactPerson', label: 'Contact Person Name', required: true, placeholder: 'Enter contact person name' },
      { name: 'mobile', label: 'Mobile', required: true, placeholder: 'Enter mobile number' },
      { name: 'status', label: 'Active or Inactive', type: 'select', required: true, options: ACTIVE_INACTIVE },
    ],
  },
  {
    key: 'consignors',
    title: 'Senders',
    path: '/consignors',
    icon: 'LocalShipping',
    apiPath: '/consignors',
    columns: [
      { field: 'name', header: 'Name' },
      { field: 'contactPerson', header: 'Contact Person' },
      { field: 'mobile', header: 'Mobile' },
      { field: 'city', header: 'City' },
      { field: 'status', header: 'Status' },
    ],
    fields: [
      { name: 'name', label: 'Sender Name', required: true },
      { name: 'code', label: 'Code' },
      { name: 'contactPerson', label: 'Contact Person', required: true },
      { name: 'mobile', label: 'Mobile', required: true },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'gstNumber', label: 'GST Number' },
      { name: 'address', label: 'Address', type: 'textarea' },
      { name: 'city', label: 'City' },
      { name: 'state', label: 'State' },
      { name: 'pincode', label: 'Pincode' },
    ],
  },
  {
    key: 'consignees',
    title: 'Receivers',
    path: '/consignees',
    icon: 'Inventory',
    apiPath: '/consignees',
    columns: [
      { field: 'name', header: 'Name' },
      { field: 'contactPerson', header: 'Contact Person' },
      { field: 'mobile', header: 'Mobile' },
      { field: 'city', header: 'City' },
      { field: 'status', header: 'Status' },
    ],
    fields: [
      { name: 'name', label: 'Receiver Name', required: true },
      { name: 'code', label: 'Code' },
      { name: 'contactPerson', label: 'Contact Person', required: true },
      { name: 'mobile', label: 'Mobile', required: true },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'gstNumber', label: 'GST Number' },
      { name: 'address', label: 'Address', type: 'textarea' },
      { name: 'city', label: 'City' },
      { name: 'state', label: 'State' },
      { name: 'pincode', label: 'Pincode' },
    ],
  },
  {
    key: 'branches',
    title: 'Branches',
    path: '/branches',
    icon: 'AccountTree',
    apiPath: '/branches',
    columns: [
      { field: 'branchName', header: 'Branch Name' },
      { field: 'branchCode', header: 'Code' },
      { field: 'contactPerson', header: 'Contact' },
      { field: 'mobile', header: 'Mobile' },
      { field: 'status', header: 'Status' },
    ],
    fields: [
      { name: 'branchName', label: 'Branch Name', required: true },
      { name: 'branchCode', label: 'Branch Code', required: true },
      { name: 'contactPerson', label: 'Contact Person', required: true },
      { name: 'mobile', label: 'Mobile', required: true },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'address', label: 'Address', type: 'textarea' },
      { name: 'city', label: 'City' },
      { name: 'state', label: 'State' },
    ],
  },
  {
    key: 'staff',
    title: 'Staff',
    path: '/staff',
    icon: 'Badge',
    apiPath: '/staff',
    columns: [
      { field: 'name', header: 'Name' },
      { field: 'staffRole', header: 'Role Type' },
      { field: 'branchName', header: 'Branch' },
      { field: 'roleName', header: 'App Role' },
      { field: 'clientNames', header: 'Clients' },
      { field: 'mobile', header: 'Mobile' },
      { field: 'status', header: 'Status' },
    ],
    fields: [
      { name: 'name', label: 'Staff Name', required: true },
      { name: 'email', label: 'Email', type: 'email', required: true },
      { name: 'mobile', label: 'Mobile', required: true },
      { name: 'staffRole', label: 'Staff Role Type', type: 'select', required: true, options: [
        { label: 'Branch Manager', value: 'manager' },
        { label: 'Operator', value: 'operator' },
        { label: 'Supervisor', value: 'supervisor' },
        { label: 'Employee', value: 'employee' },
      ]},
      { name: 'designation', label: 'Designation' },
    ],
  },
  {
    key: 'drivers',
    title: 'Drivers',
    path: '/drivers',
    icon: 'Person',
    apiPath: '/drivers',
    columns: [
      { field: 'driverName', header: 'Driver Name' },
      { field: 'clientName', header: 'Client' },
      { field: 'mobile', header: 'Mobile' },
      { field: 'licenseNumber', header: 'License' },
      { field: 'experienceYears', header: 'Experience (Yrs)', format: 'number' },
      { field: 'status', header: 'Status' },
    ],
    fields: [
      { name: 'client', label: 'Client (optional)', type: 'client', placeholder: 'Search client...', section: 'General Information' },
      { name: 'driverName', label: "Driver's Name", required: true, placeholder: "Enter Driver's Name", section: 'General Information' },
      { name: 'mobile', label: 'Mobile Number', required: true, placeholder: 'Enter Mobile Number', section: 'General Information' },
      { name: 'email', label: 'Email Address', type: 'email', placeholder: 'Enter email', section: 'General Information' },
      { name: 'licenseNumber', label: 'License Number', required: true, placeholder: 'Enter License Number', section: 'License' },
      { name: 'licenseExpiry', label: 'License Expiry Date', type: 'date', required: true, section: 'License' },
      { name: 'experienceYears', label: 'Experience (Years)', type: 'number', placeholder: 'Enter Years of Experience', section: 'General Information' },
      { name: 'aadhaarNumber', label: 'Aadhaar Number', placeholder: 'Enter Aadhaar', section: 'Identity' },
      { name: 'dateOfBirth', label: 'Date of Birth', type: 'date', section: 'Identity' },
      { name: 'address', label: 'Address', type: 'textarea', placeholder: 'Enter address', section: 'General Information', fullWidth: true },
      { name: 'joiningDate', label: 'Joining Date', type: 'date', section: 'General Information' },
      { name: 'reference1Name', label: 'Reference 1 Name', placeholder: 'Enter reference name', section: 'References' },
      { name: 'reference1Mobile', label: 'Reference 1 Mobile', placeholder: 'Enter mobile number', section: 'References' },
      { name: 'reference2Name', label: 'Reference 2 Name', placeholder: 'Enter reference name', section: 'References' },
      { name: 'reference2Mobile', label: 'Reference 2 Mobile', placeholder: 'Enter mobile number', section: 'References' },
      {
        name: 'paymentType',
        label: 'Payment Type',
        type: 'select',
        required: true,
        section: 'Payment',
        options: [
          { label: 'Monthly Salary', value: 'monthly_salary' },
          { label: 'Daily Wage', value: 'daily_wage' },
          { label: 'Trip Commission', value: 'trip_commission' },
        ],
      },
      {
        name: 'monthlySalary',
        label: 'Monthly Salary',
        type: 'number',
        required: true,
        section: 'Payment',
        placeholder: 'e.g. 25000',
        showWhen: { field: 'paymentType', equals: 'monthly_salary' },
      },
      {
        name: 'dailyWage',
        label: 'Daily Wage',
        type: 'number',
        required: true,
        section: 'Payment',
        placeholder: 'e.g. 1000',
        showWhen: { field: 'paymentType', equals: 'daily_wage' },
      },
      {
        name: 'tripCommission',
        label: 'Trip Commission (%)',
        type: 'number',
        required: true,
        section: 'Payment',
        placeholder: 'e.g. 5',
        showWhen: { field: 'paymentType', equals: 'trip_commission' },
      },
      { name: 'status', label: 'Active or Inactive', type: 'select', required: true, options: ACTIVE_INACTIVE },
    ],
  },
  {
    key: 'helpers',
    title: 'Helpers',
    path: '/helpers',
    icon: 'Groups',
    apiPath: '/helpers',
    columns: [
      { field: 'helperName', header: 'Name' },
      { field: 'mobile', header: 'Mobile' },
      { field: 'fatherName', header: 'Father Name' },
      { field: 'clientName', header: 'Client' },
      { field: 'reference1Name', header: 'Ref 1 Name' },
      { field: 'reference1Mobile', header: 'Ref 1 No.' },
      { field: 'reference2Name', header: 'Ref 2 Name' },
      { field: 'reference2Mobile', header: 'Ref 2 No.' },
      { field: 'status', header: 'Status' },
    ],
    fields: [
      { name: 'helperName', label: 'Helper Name', required: true, placeholder: 'Enter Helper Name' },
      { name: 'mobile', label: 'Mobile Number', required: true, placeholder: 'Enter Mobile Number' },
      { name: 'fatherName', label: 'Father Name', placeholder: 'Enter Father Name' },
      { name: 'address', label: 'Full Address', type: 'textarea', placeholder: 'Enter full address' },
      { name: 'reference1Name', label: 'Reference 1 Name', placeholder: 'Enter reference name' },
      { name: 'reference1Mobile', label: 'Reference 1 Contact Number', placeholder: 'Enter contact number' },
      { name: 'reference2Name', label: 'Reference 2 Name', placeholder: 'Enter reference name' },
      { name: 'reference2Mobile', label: 'Reference 2 Contact Number', placeholder: 'Enter contact number' },
      { name: 'client', label: 'Client (optional)', type: 'client', placeholder: 'Search client...' },
      { name: 'status', label: 'Active or Inactive', type: 'select', required: true, options: ACTIVE_INACTIVE },
    ],
  },
  {
    key: 'vehicles',
    title: 'Vehicles',
    path: '/vehicles',
    icon: 'DirectionsCar',
    apiPath: '/vehicles',
    columns: vehicleTableColumns,
    fields: [
      { name: 'vehicleNumber', label: 'Vehicle Number', required: true, uppercase: true, placeholder: 'e.g. MH12AB1234', section: 'General Information' },
      { name: 'typeId', label: 'Vehicle Type', type: 'vehicle_type', required: true, placeholder: 'Select type from master list', section: 'General Information' },
      { name: 'makeId', label: 'Make', type: 'vehicle_make', required: true, placeholder: 'Select make', section: 'General Information' },
      { name: 'modelId', label: 'Model', type: 'vehicle_model', required: true, placeholder: 'Select model', section: 'General Information' },
      { name: 'year', label: 'Year', type: 'number', placeholder: 'e.g. 2022', section: 'General Information' },
      { name: 'size', label: 'Size', type: 'select', required: true, options: VEHICLE_SIZE_OPTIONS, placeholder: 'Select vehicle size', section: 'General Information' },
      { name: 'capacity', label: 'Capacity (kg)', type: 'number', placeholder: 'Enter load capacity', section: 'General Information' },
      { name: 'fuelType', label: 'Fuel Type', type: 'select', required: true, options: FUEL_TYPES, section: 'General Information' },
      { name: 'maxSpeed', label: 'Max Speed (km/h)', type: 'number', placeholder: 'Enter max speed', section: 'General Information' },
      { name: 'client', label: 'Client (optional)', type: 'client', placeholder: 'Search client...', section: 'General Information' },
      {
        name: 'ownerType',
        label: 'Ownership Type',
        type: 'select',
        required: true,
        section: 'Ownership',
        options: [
          { label: 'Owned (Company)', value: 'owned' },
          { label: 'Vendor leased', value: 'leased' },
        ],
      },
      {
        name: 'vendorId',
        label: 'Choose Vendor',
        type: 'vendor',
        required: true,
        section: 'Vendor Lease',
        showWhen: { field: 'ownerType', equals: 'leased' },
        placeholder: 'Choose vehicle vendor...',
      },
      {
        name: 'vendorStartDate',
        label: 'Start Date',
        type: 'date',
        required: true,
        section: 'Vendor Lease',
        showWhen: { field: 'ownerType', equals: 'leased' },
        placeholder: 'Select start date',
      },
      {
        name: 'vendorEndDate',
        label: 'End Date',
        type: 'date',
        required: true,
        section: 'Vendor Lease',
        showWhen: { field: 'ownerType', equals: 'leased' },
        placeholder: 'Select end date',
      },
      { name: 'status', label: 'Active or Inactive', type: 'select', required: true, section: 'Ownership', options: [
        ...ACTIVE_INACTIVE,
        { label: 'Maintenance', value: 'maintenance' },
      ]},
    ],
  },
  {
    key: 'vehicle_vendors',
    title: 'Vehicle Vendors',
    path: '/vehicle-vendors',
    icon: 'Store',
    apiPath: '/vehicle-vendors',
    columns: [
      { field: 'vendorName', header: 'Vendor Name' },
      { field: 'contactPerson', header: 'Contact Person' },
      { field: 'mobile', header: 'Mobile' },
      { field: 'city', header: 'City' },
      { field: 'status', header: 'Status' },
    ],
    fields: [
      { name: 'vendorName', label: 'Vendor Name', required: true, placeholder: 'Enter Vendor Name', section: 'Vendor Details' },
      { name: 'contactPerson', label: 'Contact Person', required: true, placeholder: 'Enter Contact Person', section: 'Vendor Details' },
      { name: 'mobile', label: 'Mobile Number', required: true, placeholder: 'Enter Mobile Number', section: 'Vendor Details' },
      { name: 'contractDate', label: 'Date of Contract', type: 'date', required: true, section: 'Vendor Details', placeholder: 'Select Date' },
      { name: 'status', label: 'Status', type: 'select', required: true, options: ACTIVE_INACTIVE, section: 'Vendor Details', placeholder: 'Select Status' },
      {
        name: 'contractDocument',
        label: 'Contract Document',
        type: 'file',
        section: 'Vendor Details',
        maxSizeMB: 2,
        accept: 'application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png',
        fullWidth: true,
      },
      { name: 'address', label: 'Address', type: 'textarea', required: true, placeholder: 'Enter Address', section: 'Vendor Details', fullWidth: true },
      { name: 'city', label: 'City', placeholder: 'Enter city', section: 'Vendor Details' },
    ],
  },
  {
    key: 'trips',
    title: 'Trips',
    path: '/trips',
    icon: 'Route',
    apiPath: '/trips',
    columns: [
      { field: 'tripNumber', header: 'Trip #' },
      { field: 'tripType', header: 'Type', searchValue: (row) => TRIP_TYPE_LABELS[String(row.tripType || '')] || String(row.tripType || '') },
      { field: 'source', header: 'Source' },
      { field: 'destination', header: 'Destination' },
      { field: 'distance', header: 'KM', format: 'number' },
      { field: 'driverName', header: 'Driver' },
      { field: 'helperName', header: 'Helper' },
      { field: 'startDate', header: 'Planned', format: 'date' },
      { field: 'status', header: 'Status' },
    ],
    listColumns: [
      { field: 'tripNumber', header: 'Trip #' },
      { field: 'source', header: 'Source' },
      { field: 'destination', header: 'Destination' },
      { field: 'driverName', header: 'Driver' },
      { field: 'status', header: 'Status' },
    ],
    fields: [
      { name: 'tripNumber', label: 'Trip Number', required: true },
      { name: 'client', label: 'Client', type: 'client', required: true },
      { name: 'branch', label: 'Branch', type: 'branch', required: true },
      { name: 'tripType', label: 'Trip Type', type: 'select', required: true, options: [
        { label: 'Single Trip', value: 'one_way' },
        { label: 'Round Trip', value: 'round_trip' },
        { label: 'Multi-stop Trip', value: 'multi_stop' },
        { label: 'Daily (Recurring) Trip', value: 'daily_recurring' },
      ]},
      { name: 'source', label: 'Source', type: 'location', required: true, placeholder: 'Search India pickup location' },
      { name: 'destination', label: 'Destination', type: 'location', required: true, placeholder: 'Search India drop location' },
      { name: 'startDate', label: 'Planned Start', type: 'datetime', required: true },
      { name: 'endDate', label: 'Return Date/Time', type: 'datetime', showWhen: { field: 'tripType', equals: 'round_trip' }, placeholder: 'Planned return' },
      { name: 'returnSource', label: 'Return Trip — From', type: 'location', showWhen: { field: 'tripType', equals: 'round_trip' }, placeholder: 'Usually same as destination' },
      { name: 'returnDestination', label: 'Return Trip — To', type: 'location', showWhen: { field: 'tripType', equals: 'round_trip' }, placeholder: 'Usually same as source' },
      { name: 'recurrenceEndDate', label: 'Repeat Until', type: 'date', showWhen: { field: 'tripType', equals: 'daily_recurring' }, required: true },
      { name: 'recurrenceDays', label: 'Repeat On (0=Sun…6=Sat, comma-separated)', showWhen: { field: 'tripType', equals: 'daily_recurring' }, placeholder: '1,2,3,4,5' },
      { name: 'googleMapRoute', label: 'Google Maps Route', type: 'url', placeholder: 'Auto-filled from locations' },
      { name: 'distance', label: 'Distance (km)', type: 'number', placeholder: 'Auto from Google Maps' },
      { name: 'eta', label: 'ETA', placeholder: 'Auto from Google Maps' },
      { name: 'driver', label: 'Driver', type: 'driver', placeholder: 'Select driver' },
      { name: 'helper', label: 'Helper', type: 'helper', placeholder: 'Select helper (optional)' },
      { name: 'vehicle', label: 'Vehicle', type: 'vehicle', placeholder: 'Select vehicle (optional)' },
      { name: 'notifyWhatsApp', label: 'Notify driver on WhatsApp', type: 'select', options: [
        { label: 'Yes — send via WhatsApp Business', value: 'true' },
        { label: 'No', value: 'false' },
      ]},
      { name: 'leadName', label: 'Converted From Lead', showWhen: { field: 'leadName', notEquals: '' } },
      { name: 'actualStartTime', label: 'Actual Start', type: 'datetime' },
      { name: 'actualEndTime', label: 'Actual End', type: 'datetime' },
    ],
  },
  {
    key: 'fuel',
    title: 'Fuel',
    path: '/fuel',
    icon: 'LocalGasStation',
    apiPath: '/fuel',
    columns: [
      { field: 'date', header: 'Date', format: 'date' },
      { field: 'vehicleNumber', header: 'Licence Plate No.' },
      { field: 'fuelType', header: 'Fuel Type' },
      { field: 'fuelQuantity', header: 'Qty (L)', format: 'number' },
      { field: 'rate', header: 'Rate', format: 'currency' },
      { field: 'totalAmount', header: 'Total', format: 'currency' },
      { field: 'fuelStation', header: 'Station' },
      { field: 'odometerReading', header: 'Odometer', format: 'number' },
    ],
    fields: [
      { name: 'date', label: 'Fuel Date', type: 'date', required: true },
      { name: 'vehicle', label: 'Licence Plate No.', type: 'vehicle', required: true, placeholder: 'Select vehicle' },
      { name: 'fuelType', label: 'Fuel Type', type: 'select', required: true, options: FUEL_TYPES },
      { name: 'fuelQuantity', label: 'Quantity (Liters / Kg)', type: 'number', required: true },
      { name: 'rate', label: 'Rate (Rs.)', type: 'number', required: true },
      { name: 'fuelStation', label: 'Fuel Station', required: true },
      { name: 'odometerReading', label: 'Odometer Reading', type: 'number' },
      { name: 'employee', label: 'Filled By', type: 'user', placeholder: 'Optional' },
      { name: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
  },
  {
    key: 'maintenance',
    title: 'Service',
    path: '/maintenance',
    icon: 'Build',
    apiPath: '/maintenance',
    columns: [
      { field: 'serviceDate', header: 'Date', format: 'date' },
      { field: 'vehicleNumber', header: 'Licence Plate No.' },
      { field: 'serviceType', header: 'Service Type' },
      { field: 'workshop', header: 'Workshop' },
      { field: 'amount', header: 'Amount (Rs.)', format: 'currency' },
      { field: 'nextServiceDate', header: 'Next Service', format: 'date' },
    ],
    fields: [
      { name: 'serviceDate', label: 'Service Date', type: 'date', required: true },
      { name: 'vehicle', label: 'Licence Plate No.', type: 'vehicle', required: true },
      { name: 'serviceType', label: 'Service Type', required: true, placeholder: 'e.g. Oil change, General service' },
      { name: 'workshop', label: 'Workshop', required: true },
      { name: 'amount', label: 'Amount (Rs.)', type: 'number', required: true },
      { name: 'nextServiceDate', label: 'Next Service Date', type: 'date' },
      { name: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
  },
  {
    key: 'puncture',
    title: 'Puncture',
    path: '/puncture',
    icon: 'TireRepair',
    apiPath: '/puncture',
    columns: [
      { field: 'punctureDate', header: 'Date', format: 'date' },
      { field: 'vehicleNumber', header: 'Licence Plate No.' },
      { field: 'location', header: 'Location' },
      { field: 'tyrePosition', header: 'Tyre Position' },
      { field: 'repairShop', header: 'Repair Shop' },
      { field: 'cost', header: 'Cost (Rs.)', format: 'currency' },
    ],
    fields: [
      { name: 'punctureDate', label: 'Date', type: 'date', required: true },
      { name: 'vehicle', label: 'Licence Plate No.', type: 'vehicle', required: true },
      { name: 'location', label: 'Location' },
      { name: 'tyrePosition', label: 'Tyre Position', placeholder: 'e.g. Front left' },
      { name: 'repairShop', label: 'Repair Shop' },
      { name: 'cost', label: 'Cost (Rs.)', type: 'number' },
      { name: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
  },
  {
    key: 'cash_memo',
    title: 'Cash Book',
    path: '/cash-memo',
    icon: 'ReceiptLong',
    apiPath: '/cash-memo',
    columns: [
      { field: 'memoDate', header: 'Date', format: 'date' },
      { field: 'vehicleNumber', header: 'Licence Plate No.' },
      { field: 'partyName', header: 'Party' },
      { field: 'routeOrPlace', header: 'Route / Place' },
      { field: 'description', header: 'Description' },
      { field: 'entryType', header: 'Type' },
      { field: 'amount', header: 'Amount (Rs.)', format: 'currency' },
      { field: 'paymentMode', header: 'Mode' },
      { field: 'monthKey', header: 'Month' },
    ],
    fields: [
      { name: 'memoDate', label: 'Memo Date', type: 'date', required: true },
      { name: 'memoNumber', label: 'Memo Number', placeholder: 'Optional' },
      { name: 'vehicle', label: 'Licence Plate No.', type: 'vehicle', placeholder: 'Optional — select vehicle' },
      { name: 'partyName', label: 'Party / Client' },
      { name: 'routeOrPlace', label: 'Route / Place' },
      { name: 'description', label: 'Description', type: 'textarea', required: true },
      { name: 'amount', label: 'Amount (Rs.)', type: 'number', required: true },
      { name: 'entryType', label: 'Entry Type', type: 'select', required: true, options: [
        { label: 'Debit (Expense)', value: 'debit' },
        { label: 'Credit (Income)', value: 'credit' },
      ]},
      { name: 'paymentMode', label: 'Payment Mode', type: 'select', options: [
        { label: 'Cash', value: 'cash' },
        { label: 'UPI', value: 'upi' },
        { label: 'NEFT', value: 'neft' },
        { label: 'Cheque', value: 'cheque' },
        { label: 'Other', value: 'other' },
      ]},
      { name: 'paidTo', label: 'Paid To / Received From' },
      { name: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
  },
  {
    key: 'fastag',
    title: 'FASTag',
    path: '/fastag',
    icon: 'Toll',
    apiPath: '/fastag',
    columns: [
      { field: 'tagAccountNo', header: 'Tag Account No.' },
      { field: 'vehicleNumber', header: 'Licence Plate No.' },
      { field: 'group', header: 'Group' },
      { field: 'tripCount', header: 'Trip Count' },
      { field: 'openingBalance', header: 'Opening Balance (Rs.)', format: 'currency' },
      { field: 'credit', header: 'Credit (Rs.)', format: 'currency' },
      { field: 'debit', header: 'Debit (Rs.)', format: 'currency' },
      { field: 'closingBalance', header: 'Closing Balance (Rs.)', format: 'currency' },
      { field: 'txnDate', header: 'Statement Date', format: 'date' },
      { field: 'monthKey', header: 'Month' },
    ],
    fields: [
      { name: 'txnDate', label: 'Statement Date', type: 'date', required: true },
      { name: 'tagAccountNo', label: 'Tag Account No.', required: true, placeholder: 'e.g. 36388639' },
      { name: 'vehicle', label: 'Licence Plate No.', type: 'vehicle', required: true, placeholder: 'Select vehicle' },
      { name: 'group', label: 'Group', placeholder: 'Default' },
      { name: 'tripCount', label: 'Trip Count', type: 'number' },
      { name: 'openingBalance', label: 'Opening Balance (Rs.)', type: 'number' },
      { name: 'credit', label: 'Credit (Rs.)', type: 'number' },
      { name: 'debit', label: 'Debit (Rs.)', type: 'number' },
      { name: 'closingBalance', label: 'Closing Balance (Rs.)', type: 'number' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
  },
  {
    key: 'battery_maintenance',
    title: 'Battery',
    path: '/battery-maintenance',
    icon: 'BatteryChargingFull',
    apiPath: '/battery-maintenance',
    columns: [
      { field: 'serviceDate', header: 'Date', format: 'date' },
      { field: 'vehicleNumber', header: 'Licence Plate No.' },
      { field: 'workType', header: 'Work' },
      { field: 'batteryBrand', header: 'Brand' },
      { field: 'batteryCapacity', header: 'Capacity' },
      { field: 'workshop', header: 'Workshop' },
      { field: 'amount', header: 'Amount (Rs.)', format: 'currency' },
      { field: 'monthKey', header: 'Month' },
    ],
    fields: [
      { name: 'serviceDate', label: 'Service Date', type: 'date', required: true },
      { name: 'vehicle', label: 'Licence Plate No.', type: 'vehicle', required: true },
      { name: 'workType', label: 'Work Type', type: 'select', options: [
        { label: 'Replacement', value: 'replacement' },
        { label: 'Charging', value: 'charging' },
        { label: 'Terminal Clean', value: 'terminal_clean' },
        { label: 'Warranty', value: 'warranty' },
        { label: 'Other', value: 'other' },
      ]},
      { name: 'batteryBrand', label: 'Battery Brand' },
      { name: 'batteryCapacity', label: 'Capacity', placeholder: 'e.g. 150 Ah' },
      { name: 'workshop', label: 'Workshop' },
      { name: 'amount', label: 'Amount (Rs.)', type: 'number', required: true },
      { name: 'warrantyMonths', label: 'Warranty (Months)', type: 'number' },
      { name: 'nextCheckDate', label: 'Next Check Date', type: 'date' },
      { name: 'paymentMode', label: 'Payment Mode', type: 'select', options: [
        { label: 'Cash', value: 'cash' },
        { label: 'UPI', value: 'upi' },
        { label: 'NEFT', value: 'neft' },
        { label: 'Other', value: 'other' },
      ]},
      { name: 'paidTo', label: 'Paid To' },
      { name: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
  },
  {
    key: 'invoices',
    title: 'Invoices',
    path: '/invoices',
    icon: 'Receipt',
    apiPath: '/invoices',
    columns: [
      { field: 'invoiceNumber', header: 'Invoice #' },
      { field: 'subtotal', header: 'Subtotal' },
      { field: 'taxAmount', header: 'Tax' },
      { field: 'totalAmount', header: 'Total' },
      { field: 'paymentStatus', header: 'Payment' },
    ],
    fields: [
      { name: 'invoiceNumber', label: 'Invoice Number', required: true },
      { name: 'subtotal', label: 'Subtotal', type: 'number', required: true },
      { name: 'taxPercentage', label: 'Tax %', type: 'number' },
      { name: 'totalAmount', label: 'Total Amount', type: 'number', required: true },
      { name: 'paymentStatus', label: 'Payment Status', type: 'select', options: [
        { label: 'Pending', value: 'pending' },
        { label: 'Paid', value: 'paid' },
        { label: 'Partial', value: 'partial' },
      ]},
    ],
  },
  {
    key: 'bilty',
    title: 'Bilty (LR)',
    path: '/bilty',
    icon: 'Description',
    apiPath: '/bilty',
    columns: [
      { field: 'biltyNumber', header: 'Bilty #' },
      { field: 'source', header: 'Source' },
      { field: 'destination', header: 'Destination' },
      { field: 'weight', header: 'Weight' },
      { field: 'freightAmount', header: 'Freight' },
    ],
    fields: [
      { name: 'biltyNumber', label: 'Bilty Number', required: true },
      { name: 'source', label: 'Source', type: 'location', required: true, placeholder: 'Search India origin' },
      { name: 'destination', label: 'Destination', type: 'location', required: true, placeholder: 'Search India destination' },
      { name: 'weight', label: 'Weight (kg)', type: 'number', required: true },
      { name: 'freightAmount', label: 'Freight Amount', type: 'number', required: true },
      { name: 'consignorName', label: 'Sender Name' },
      { name: 'consigneeName', label: 'Receiver Name' },
    ],
  },
];

export const tenantModules = coreModules.map(withStatus);

export const getModuleAPI = (apiPath: string) => {
  const apis: Record<string, ReturnType<typeof import('../services/api').clientAPI.getAll>> = {};
  // resolved dynamically in CrudModulePage via api service
  return apiPath;
};
